import { createContext, useContext, useEffect, useRef } from "react";

export type PageShortcut = { keys: string[]; label: string };
export type PageShortcutSection = { heading: string; items: PageShortcut[] };

export interface PageShortcutsContextValue {
  sections: PageShortcutSection[];
  register: (id: string, sections: PageShortcutSection[]) => void;
  unregister: (id: string) => void;
}

export const PageShortcutsContext = createContext<PageShortcutsContextValue | null>(null);

export const usePageShortcutsRegistry = () => {
  const ctx = useContext(PageShortcutsContext);
  return ctx?.sections ?? [];
};

/**
 * Register page-specific shortcuts to be displayed in the global ? dialog.
 * Pass a stable id (e.g. the page route) and the sections to display.
 */
export const useRegisterPageShortcuts = (id: string, sections: PageShortcutSection[]) => {
  const ctx = useContext(PageShortcutsContext);
  // Keep the context in a ref so the effect never re-runs just because the
  // provider rebuilt its value (which it does on every registry write).
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  // Serialize for stable identity
  const key = JSON.stringify(sections);
  useEffect(() => {
    const current = ctxRef.current;
    if (!current) return;
    current.register(id, JSON.parse(key) as PageShortcutSection[]);
    return () => ctxRef.current?.unregister(id);
  }, [id, key]);

};
